/**
 * Motion helpers — wraps anime.js with theme-aware defaults so all
 * animations across the app pull their duration + easing from the
 * design tokens (`_tokens.scss` → `--theme-duration-*` /
 * `--theme-easing-*`). Single source of truth for motion same as
 * for color.
 *
 * Why a wrapper (vs calling anime() directly):
 *   - Consumers don't hard-code `300ms` or `cubic-bezier(...)` — they
 *     pass `'quick'` / `'decel'` and the wrapper resolves to the
 *     current theme value. Operators rebrand by editing tokens.
 *   - One place to swap animation libs if anime.js ever stops
 *     being the right tool. Call sites stay unchanged.
 *
 * Usage:
 *
 *   import { animate, durations, easings } from '@/lib/motion'
 *
 *   animate(el, {
 *     translateY: [10, 0],
 *     opacity: [0, 1],
 *     duration: durations.normal,
 *     easing: easings.decel,
 *   })
 */
import { animate as animeAnimate, createTimeline } from 'animejs'

/**
 * Theme-token duration aliases. Read from the live CSS custom
 * property at call time, NOT cached — supports operator runtime
 * theme switches without page reload.
 */
export const durations = {
  get instant(): number {
    return readDurationVar('instant', 60)
  },
  get quick(): number {
    return readDurationVar('quick', 140)
  },
  get normal(): number {
    return readDurationVar('normal', 220)
  },
  get slow(): number {
    return readDurationVar('slow', 340)
  },
  get slower(): number {
    return readDurationVar('slower', 500)
  },
}

/**
 * Theme-token easing aliases. Returns the raw `cubic-bezier(...)`
 * string from the CSS variable so anime.js can parse it directly.
 *
 * The `panel` curve is THE Linear signature panel-presentation
 * curve (`cubic-bezier(.32, .72, 0, 1)`) — also iOS's standard
 * sheet curve. Reserve for major panel transitions (drawers,
 * dialogs, sheets). Pair with `durations.slower` (500ms).
 *
 * The `out-*` / `in-*` / `in-out-*` aliases mirror Linear's full
 * curve catalog for fine-grained control when the high-level
 * `decel` / `default` don't fit the motion intent.
 */
export const easings = {
  get decel(): string {
    return readEasingVar('decel', 'cubic-bezier(0.23, 1, 0.32, 1)')
  },
  get default(): string {
    return readEasingVar('default', 'cubic-bezier(0.645, 0.045, 0.355, 1)')
  },
  get spring(): string {
    return readEasingVar('spring', 'cubic-bezier(0.34, 1.56, 0.64, 1)')
  },
  get panel(): string {
    return readEasingVar('panel', 'cubic-bezier(0.32, 0.72, 0, 1)')
  },
  get linear(): string {
    return 'linear'
  },
  // Fine-grained curves — read straight from CSS vars. Use these
  // when you need an exact match against Linear's published catalog.
  get inQuad(): string {
    return readEasingVar('in-quad', 'cubic-bezier(0.55, 0.085, 0.68, 0.53)')
  },
  get inCubic(): string {
    return readEasingVar('in-cubic', 'cubic-bezier(0.55, 0.055, 0.675, 0.19)')
  },
  get outQuart(): string {
    return readEasingVar('out-quart', 'cubic-bezier(0.165, 0.84, 0.44, 1)')
  },
  get outQuint(): string {
    return readEasingVar('out-quint', 'cubic-bezier(0.23, 1, 0.32, 1)')
  },
  get outExpo(): string {
    return readEasingVar('out-expo', 'cubic-bezier(0.19, 1, 0.22, 1)')
  },
  get inOutQuart(): string {
    return readEasingVar('in-out-quart', 'cubic-bezier(0.77, 0, 0.175, 1)')
  },
  get inOutQuint(): string {
    return readEasingVar('in-out-quint', 'cubic-bezier(0.86, 0, 0.07, 1)')
  },
}

/**
 * Direct passthrough to anime.js's `animate` — kept so consumers can
 * call `animate(...)` with the same signature anime.js documents.
 * Most call sites will use the typed `durations` / `easings` aliases
 * for the duration + easing args.
 */
export const animate = animeAnimate

/**
 * Timeline composer — same passthrough to anime.js's
 * `createTimeline`. Use for choreographed multi-step animations
 * (modal enter → content cascade, page transitions, etc.).
 */
export const timeline = createTimeline

/**
 * Read a CSS `--theme-duration-*` custom property and convert
 * "Nms" → number. Returns the fallback if the var isn't present
 * (SSR, or before main.scss has loaded).
 */
function readDurationVar(name: string, fallback: number): number {
  if (typeof window === 'undefined') return fallback
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue(`--theme-duration-${name}`)
    .trim()
  if (!raw) return fallback
  const numericPart = raw.replace(/ms$/i, '').trim()
  const value = Number(numericPart)
  return Number.isFinite(value) ? value : fallback
}

/**
 * Read a CSS `--theme-easing-*` custom property. Returns the
 * fallback if missing.
 */
function readEasingVar(name: string, fallback: string): string {
  if (typeof window === 'undefined') return fallback
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue(`--theme-easing-${name}`)
    .trim()
  return raw || fallback
}
